const API_BASE = (import.meta?.env?.VITE_API_BASE || "").replace(/\/$/, "");
const LS_KEY = "mamute.ai.config.v1";

function apiUrl(path) {
  // Permite rodar atrás de proxy customizado.
  // Ex.: VITE_API_BASE="http://127.0.0.1:5000" ou "" (mesma origem)
  return `${API_BASE}${path}`;
}

function loadLocalStore() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
}

function saveLocalStore(store) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(store || {}));
  } catch {
    // ignore
  }
}

function getLocalPlainKey(empresaId) {
  const store = loadLocalStore();
  const c = store?.[empresaId];
  const k = typeof c?.apiKey === "string" ? c.apiKey.trim() : "";
  return k;
}

// OBS: usado somente no modo CWIES/sem-backend.
// Em produção, a chave deve ficar no servidor.
export function getStoredAiKeyForTesting(empresaId) {
  return getLocalPlainKey(empresaId);
}

function getLocalConfig(empresaId) {
  const store = loadLocalStore();
  const c = store?.[empresaId] || {};
  return {
    enabled: !!c.enabled,
    hasKey: !!c.hasKey,
    modelDefault: c.modelDefault || "gpt-5-mini",
    updatedAt: c.updatedAt || null,
    _source: "local",
  };
}

function setLocalConfig({ empresaId, apiKey, enabled, modelDefault }) {
  const store = loadLocalStore();
  const now = new Date().toISOString();
  const prev = store?.[empresaId] || {};

  const apiKeyTrim = typeof apiKey === "string" ? apiKey.trim() : "";
  const prevKeyTrim = typeof prev.apiKey === "string" ? prev.apiKey.trim() : "";
  const finalKey = apiKeyTrim || prevKeyTrim || "";

  const hasKey = !!finalKey;

  store[empresaId] = {
    enabled: !!enabled,
    hasKey,
    modelDefault: modelDefault || prev.modelDefault || "gpt-5-mini",
    updatedAt: now,
    apiKey: finalKey, // SOMENTE PARA TESTE (CWIES sem backend)
  };

  saveLocalStore(store);
  return getLocalConfig(empresaId);
}

function deleteLocalKey(empresaId) {
  const store = loadLocalStore();
  const now = new Date().toISOString();
  const prev = store?.[empresaId] || {};
  store[empresaId] = {
    ...prev,
    enabled: false,
    hasKey: false,
    apiKey: "",
    updatedAt: now,
  };
  saveLocalStore(store);
  return true;
}

async function readMaybeJson(res) {
  const raw = await res.text();
  let data = null;
  try {
    data = JSON.parse(raw);
  } catch {
    data = null;
  }
  const looksHtml = typeof raw === "string" && raw.trim().startsWith("<");
  return { raw, data, looksHtml };
}

function isNetworkishError(err) {
  const msg = String(err?.message || err || "").toLowerCase();
  return (
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("load failed") ||
    msg.includes("html_response")
  );
}

export async function apiGetAiConfig(empresaId) {
  try {
    const res = await fetch(apiUrl(`/api/ai/config/${encodeURIComponent(empresaId)}`));
    const { data, looksHtml } = await readMaybeJson(res);
    if (looksHtml) throw new Error("HTML_RESPONSE");
    if (!res.ok || !data?.ok) {
      throw new Error(data?.error || "Falha ao carregar config de IA");
    }
    return data.config;
  } catch (err) {
    // Se o CWIES estiver rodando só o front (sem backend), cai aqui e usa localStorage.
    if (isNetworkishError(err)) return getLocalConfig(empresaId);
    throw err;
  }
}

export async function apiSaveAiConfig({ empresaId, apiKey, enabled, modelDefault }) {
  try {
    const res = await fetch(apiUrl(`/api/ai/config/save`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ empresaId, apiKey, enabled, modelDefault }),
    });
    const { data, looksHtml } = await readMaybeJson(res);
    if (looksHtml) throw new Error("HTML_RESPONSE");
    if (!res.ok || !data?.ok) {
      throw new Error(data?.error || "Falha ao salvar config de IA");
    }
    return data.config;
  } catch (err) {
    if (isNetworkishError(err)) {
      // Modo teste (CWIES sem terminal/backend): salva localmente.
      return setLocalConfig({ empresaId, apiKey, enabled, modelDefault });
    }
    throw err;
  }
}

export async function apiDeleteAiKey(empresaId) {
  try {
    const res = await fetch(apiUrl(`/api/ai/config/delete`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ empresaId }),
    });
    const { data, looksHtml } = await readMaybeJson(res);
    if (looksHtml) throw new Error("HTML_RESPONSE");
    if (!res.ok || !data?.ok) {
      throw new Error(data?.error || "Falha ao remover chave");
    }
    return true;
  } catch (err) {
    if (isNetworkishError(err)) return deleteLocalKey(empresaId);
    throw err;
  }
}

export async function apiTestAi({ empresaId, apiKey }) {
  try {
    const res = await fetch(apiUrl(`/api/ai/test`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ empresaId, apiKey }),
    });
    const { data, looksHtml } = await readMaybeJson(res);
    if (looksHtml) throw new Error("HTML_RESPONSE");
    if (!res.ok || !data?.ok) {
      throw new Error(data?.error || "Falha ao testar IA");
    }
    return true;
  } catch (err) {
    if (!isNetworkishError(err)) throw err;

    // Modo teste (sem backend): validação leve (formato).
    const key = (typeof apiKey === "string" ? apiKey.trim() : "") || getLocalPlainKey(empresaId);
    if (!key) throw new Error("Cole uma API Key para testar.");
    if (!key.startsWith("sk-") || key.length < 30) throw new Error("API Key parece inválida (formato).");
    return true;
  }
}

// Chat genérico (suporta histórico). Tenta o backend primeiro; se falhar, usa chamada direta ao OpenAI
// quando existir uma chave em localStorage (somente CWIES/sem-backend).
export async function apiChatAi({
  empresaId,
  message,
  messages,
  model,
  system,
}) {
  // 1) Backend (preferido)
  try {
    const res = await fetch(apiUrl(`/api/ai/chat`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ empresaId, message, messages, model, system }),
    });
    const { data, looksHtml } = await readMaybeJson(res);
    if (looksHtml) throw new Error("HTML_RESPONSE");
    if (!res.ok || !data?.ok) {
      throw new Error(data?.error || "Falha ao chamar IA");
    }
    return { text: String(data.text || "").trim(), _source: "backend" };
  } catch (err) {
    // Se for erro de rede/HTML, tenta fallback direto
    if (!isNetworkishError(err)) throw err;
  }

  // 2) Fallback direto ao OpenAI (somente para teste no CWIES)
  const key = getLocalPlainKey(empresaId);
  if (!key) throw new Error("Configure uma API Key (Opções → IA) para usar no modo teste.");

  const chosenModel = (model || "gpt-5-mini").trim();
  const input = Array.isArray(messages) && messages.length
    ? [
        ...(system ? [{ role: "system", content: String(system) }] : []),
        ...messages.map((m) => ({
          role: m?.role || "user",
          content: String(m?.content || ""),
        })),
      ]
    : [
        ...(system ? [{ role: "system", content: String(system) }] : []),
        { role: "user", content: String(message || "") },
      ];

  const resp = await fetch("https://api.openai.com/v1/responses", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${key}`,
    },
    body: JSON.stringify({
      model: chosenModel,
      input,
    }),
  });

  const { data } = await readMaybeJson(resp);
  if (!resp.ok || !data) {
    throw new Error(
      (data && (data.error?.message || data.error)) ||
        `Falha ao chamar OpenAI (HTTP ${resp.status})`
    );
  }

  // Extrai texto do output
  const output = data.output || [];
  let text = "";
  for (const item of output) {
    if (item?.type === "message") {
      for (const c of item.content || []) {
        if (c?.type === "output_text") text += c.text;
      }
    }
  }
  return { text: String(text || "").trim(), _source: "browser" };
}
